import React, { useEffect, useMemo, useRef, useState } from "react";
import useAxios from "../../utils/useAxios";
import { useTranslation } from "react-i18next";
import { useInView } from "react-intersection-observer";
import { Container } from "react-bootstrap";
import Skeleton from "react-loading-skeleton";
import FeatherIcon from "feather-icons-react";
import { CustomModal } from "../../components/CustomModel/CustomModal";
import RatingForm from "./RatingForm";
import RatingExportModal from "./RatingExportModal";
import useModal from "../../hooks/useModal";
import MenuList from "../../components/MenuList";
import { getInitials } from "../../utils/getInitials";
import { toast } from "react-toastify";
import { useHistory } from "react-router-dom";
import debounce from "lodash.debounce";
import { prefetchUrl } from "../../utils/tabPrefetch";
import { CompactTableRow } from "../../components/CompactTableRow";
import useCompactToolbar from "../../hooks/useCompactToolbar";

const RatingsList = () => {
    const { t } = useTranslation();
    const api = useAxios();
    const { modalIsOpen, setIsOpen, openModal } = useModal(false);
    const { ref, inView } = useInView({ threshold: 1 });
    const requestIdRef = useRef(0);
    const searchInputRef = useRef(null);
    // Compact Table is a user-facing-only density preference (see
    // TournamentsList.js/ClubsList.js) - this list is admin-only with no
    // public counterpart to ever show it in, so it's always off here
    // regardless of the stored setting.
    const compactTable = false;

    const [ratings, setRatings] = useState([]);
    const [currentPage, setCurrentPage] = useState("");
    const [loader, setLoader] = useState(false);
    const [skeletonCards, setSkeletonCards] = useState(10);
    const [editingRating, setEditingRating] = useState(null);
    const [exportModalRatingId, setExportModalRatingId] = useState(null);
    const history = useHistory();
    const [clubs, setClubs] = useState([]);
    const [hasError, setHasError] = useState(false);
    // Uncontrolled search input has no state of its own to read from
    // elsewhere - tracked separately so hasActiveSearch (further down) can
    // tell "there's a query in progress" apart from empty.
    const [searchQuery, setSearchQuery] = useState("");

    useEffect(() => {
        document.title = t("Ratings");
    }, []);

    const fetchClubs = async () => {
        try {
            const res = await api.get("/api/v1.0/clubs/?limit=100");
            const results = res.data.results || [];
            setClubs(results);
            return results;
        } catch (e) {
            console.error(e);
            return null;
        }
    };

    useEffect(() => {
        fetchClubs();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // See ClubsList.js's own copy of this same formula for the full story:
    // a Compact Table row runs ~40px tall, so the backend's own default
    // page size (sized for cards) leaves a compact list several screens
    // short of a full viewport, forcing extra scroll-triggered fetches
    // just to fill it once.
    const compactPageSize = () => Math.max(10, Math.ceil(window.innerHeight / 40) + 5);

    const resetAndLoad = (query = "") => {
        requestIdRef.current += 1;
        setRatings([]);
        setSkeletonCards(10);
        setHasError(false);
        const params = new URLSearchParams();
        if (query && query.length >= 2) params.set("search", query);
        if (compactTable) params.set("limit", compactPageSize());
        setCurrentPage(`/api/v1.0/ratings/${params.toString() ? "?" + params.toString() : ""}`);
    };

    useEffect(() => {
        resetAndLoad();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const debouncedSearch = useMemo(() => debounce((val) => resetAndLoad(val), 400), []);
    useEffect(() => () => debouncedSearch.cancel(), [debouncedSearch]);

    useEffect(() => {
        if ((inView || ratings.length === 0) && currentPage && !loader && !hasError) {
            getRatings();
        }
    }, [inView, currentPage, loader, hasError]);

    const getRatings = async () => {
        const reqId = requestIdRef.current;
        setLoader(true);
        try {
            const res = await prefetchUrl(api, currentPage);
            if (reqId !== requestIdRef.current) return;

            const results = res.data.results || [];

            setRatings(prev => {
                const existingIds = new Set(prev.map(r => r.id));
                const newItems = results.filter(r => !existingIds.has(r.id));
                return [...prev, ...newItems];
            });
            setCurrentPage(res.data.next ? res.data.next.replace(/^https?:\/\/[^/]+/, '') : null);
            setSkeletonCards(res.data.next ? 10 : 0);
        } catch (e) {
            if (reqId !== requestIdRef.current) return;
            console.error(e);
            setHasError(true);
        } finally {
            if (reqId === requestIdRef.current) {
                setLoader(false);
            }
        }
    };

    const handleRetry = () => {
        setHasError(false);
        getRatings();
    };

    const handleCreate = async () => {
        // Re-fetch instead of trusting the mount-time `clubs` state: that
        // state can still be empty if this fires before the initial request
        // resolves, or stale if a club was created elsewhere since mount -
        // either way it falsely redirected to "create a club first" even
        // though the club already existed.
        const latestClubs = await fetchClubs();
        if ((latestClubs ?? clubs).length < 1) {
            toast.warning(t("Create a club first"));
            history.push("/admin/clubs");
            return;
        }
        setEditingRating(null);
        openModal();
    };

    const handleShare = async (ratingId) => {
        try {
            const res = await api.get(`/api/v1.0/ratings/${ratingId}/table/`);
            const tournaments = res.data?.tournaments || [];
            if (tournaments.length > 0) {
                const lastTournament = tournaments[tournaments.length - 1];
                const link = `${window.location.origin}/tournaments/${lastTournament.id}?tab=rating&show_rating=true`;
                navigator.clipboard.writeText(link);
                toast.success(t("Link copied to clipboard"));
            } else {
                toast.warning(t("This rating has no tournaments yet."));
            }
        } catch (error) {
            console.error(error);
            toast.error(t("Error generating link"));
        }
    };

    const { toolbarSentinelRef, progress: toolbarProgress, activated: toolbarActivated, collapse: collapseToolbar } = useCompactToolbar({
        onExpand: () => {
            window.scrollTo(0, 0);
            searchInputRef.current?.focus();
        },
        onCreate: handleCreate
    });

    // See TournamentsList.js/ClubsList.js's own copy of this same formula
    // for the full story: paddingBottom mirrors paddingTop's 10*(1-progress)
    // once activated (10px expanded, shrinking to 0 once scrolled away,
    // flush against the header again) - but before the row's ever been
    // opened this mount, progress is permanently pinned at 1 regardless of
    // scroll position, which would collapse this formula to a flat 0 the
    // entire time the page is scrolled. The flat 10px override is only for
    // that one specific "never opened yet" state, so it doesn't pin a
    // permanent gap-colored strip under the header for the rest of the
    // scroll.
    // With an active search the row stays fully expanded regardless of
    // scroll, instead of auto-collapsing away - same fix as
    // TournamentsList.js's own hasActiveSearch/effectiveToolbarProgress.
    const hasActiveSearch = searchQuery.trim().length > 0;
    const effectiveToolbarProgress = hasActiveSearch ? 0 : toolbarProgress;

    const toolbarStyle = {
        paddingTop: `${5 * (1 - effectiveToolbarProgress)}px`,
        paddingBottom: toolbarActivated ? `${10 * (1 - effectiveToolbarProgress)}px` : "10px"
    };
    const searchRowStyle = {
        maxHeight: `${60 * (1 - effectiveToolbarProgress)}px`,
        opacity: 1 - effectiveToolbarProgress,
        pointerEvents: effectiveToolbarProgress >= 0.99 ? "none" : "auto"
    };

    return (
        <Container className="page_container">
            <div ref={toolbarSentinelRef} style={{ height: "1px" }} />
            <div className="sticky-toolbar" style={toolbarStyle}>
                <nav className="tabs-container toolbar-search-row" style={searchRowStyle}>
                    <ul className="tabs">
                        <li style={{ position: "relative", width: "100%" }}>
                            <FeatherIcon icon="search" size={16} className="search-icon" />
                            <input
                                ref={searchInputRef}
                                className="search"
                                type="text"
                                placeholder={t("Ratings search: name...")}
                                onKeyUp={e => { setSearchQuery(e.target.value); debouncedSearch(e.target.value); }}
                                onBlur={e => { if (!e.target.value) collapseToolbar(); }}
                                style={{ paddingLeft: "30px" }}
                            />
                        </li>
                    </ul>
                </nav>
            </div>

            {compactTable ? (
                <div className="trows">
                    {ratings.map((rating, idx) => {
                        const dateRange = `${rating.start_date ? new Date(rating.start_date).toLocaleDateString('uk-UA') : '—'} - ${rating.end_date ? new Date(rating.end_date).toLocaleDateString('uk-UA') : '—'}`;
                        return (
                            <CompactTableRow
                                key={rating.id}
                                variant="entity"
                                zebra={idx % 2 === 1}
                                title={rating.name}
                                subtitle={[rating.club_name, dateRange].filter(Boolean).join(" · ")}
                                onClick={() => history.push(`/admin/ratings/${rating.id}`)}
                                actions={(
                                    <MenuList
                                        deleteEndpoint={`/api/v1.0/ratings/${rating.id}`}
                                        onDeleted={() =>
                                            setRatings(prev => prev.filter(r => r.id !== rating.id))
                                        }
                                        successMessage="Rating deleted"
                                        errorMessage="Error deleting rating"
                                        is_edit
                                        onEdit={() => {
                                            setEditingRating(rating);
                                            openModal();
                                        }}
                                        is_share
                                        onShare={() => handleShare(rating.id)}
                                        is_export
                                        onExport={() => setExportModalRatingId(rating.id)}
                                        asModal
                                    />
                                )}
                            />
                        );
                    })}
                    {(loader || ratings.length === 0) && skeletonCards > 0 && (
                        Array.from({ length: skeletonCards }).map((_, i) => (
                            <div className="trow trow-entity" key={`skel-rating-${i}`} style={{ pointerEvents: "none" }}>
                                <Skeleton circle width={20} height={20} />
                                <Skeleton width="60%" height={12} />
                                <span />
                            </div>
                        ))
                    )}
                </div>
            ) : (
            <div className="entity-cards">
                {ratings.map((rating) => (
                    <div className="entity-card" key={rating.id} onClick={() => history.push(`/admin/ratings/${rating.id}`)}>
                        <div className="entity-card-actions" onClick={e => e.stopPropagation()}>
                            <MenuList
                                deleteEndpoint={`/api/v1.0/ratings/${rating.id}`}
                                onDeleted={() =>
                                    setRatings(prev => prev.filter(r => r.id !== rating.id))
                                }
                                successMessage="Rating deleted"
                                errorMessage="Error deleting rating"
                                is_edit
                                onEdit={() => {
                                    setEditingRating(rating);
                                    openModal();
                                }}
                                is_share
                                onShare={() => handleShare(rating.id)}
                                is_export
                                onExport={() => setExportModalRatingId(rating.id)}
                                asModal
                            />
                        </div>
                        <div className="entity-card-top">
                            <div className="entity-card-thumb">{getInitials(rating.name)}</div>
                            <div className="entity-card-titles">
                                <div className="entity-card-title">{rating.name}</div>
                                <div className="entity-card-subtitle">{rating.club_name}</div>
                            </div>
                        </div>
                        <div className="entity-card-line">
                            {rating.start_date ? new Date(rating.start_date).toLocaleDateString('uk-UA') : '—'} -{' '}
                            {rating.end_date ? new Date(rating.end_date).toLocaleDateString('uk-UA') : '—'}
                        </div>
                    </div>
                ))}
                {(loader || ratings.length === 0) && skeletonCards > 0 && Array.from({ length: skeletonCards }).map((_, i) => (
                    <div className="entity-card" key={`skel-rating-${i}`} style={{ pointerEvents: "none" }}>
                        <div className="entity-card-top">
                            <div className="entity-card-thumb" style={{ background: "transparent" }}>
                                <Skeleton circle width={52} height={52} />
                            </div>
                            <div className="entity-card-titles">
                                <div className="entity-card-title"><Skeleton width="65%" height={15} /></div>
                                <div className="entity-card-subtitle"><Skeleton width="45%" height={12} style={{ marginTop: 4 }} /></div>
                            </div>
                        </div>
                        <div className="entity-card-line"><Skeleton width="50%" height={11} /></div>
                    </div>
                ))}
            </div>
            )}

            {!loader && hasError && (
                <div className="not-found">
                    <h2><FeatherIcon icon="alert-triangle" size={106}/></h2>
                    <h4>{t("Something went wrong")}</h4>
                    <p>{t("Failed to load list. Please check your connection and try again.")}</p>
                    <a href="#" className="action-button btn-green" onClick={(e) => { e.preventDefault(); handleRetry(); }}>{t("Try again")}</a>
                </div>
            )}

            <CustomModal isOpen={modalIsOpen} onClose={() => setIsOpen(false)}>
                <RatingForm
                    rating={editingRating}
                    onClose={() => setIsOpen(false)}
                    onSuccess={(item) => {
                        setRatings(prev => {
                            const idx = prev.findIndex(r => r.id === item.id);
                            if (idx !== -1) {
                                const copy = [...prev];
                                copy[idx] = item;
                                return copy;
                            }
                            return [item, ...prev];
                        });
                        setIsOpen(false);
                    }}
                    clubs={clubs}
                />
            </CustomModal>

            <RatingExportModal
                isOpen={!!exportModalRatingId}
                onClose={() => setExportModalRatingId(null)}
                ratingId={exportModalRatingId}
            />

            <div ref={ref} />
        </Container>
    );
};

export default RatingsList;
